import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CalendarClock, History, Loader2, MessageSquare, Plus, Settings } from 'lucide-react';
import AIChat from './AIChat';
import AssistantDock from './AssistantDock';
import HIGHLIGHT_PROMPTS from './promptLibrary';
import { useAssistantContext } from '../../assistant/context';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import { getLocations } from '../../utils/locationApi';
import { deleteAIChatSession, getAIChatSessions } from '../../utils/api';
import './AssistantLauncher.css';

const CORNERS = ['bottom-right', 'bottom-left', 'top-right', 'top-left'];
const CORNER_KEY = 'lodgeic.assistant.corner';
const POSITION_KEY = 'lodgeic.assistant.position';
const LAUNCHER_LABEL = 'Ask Lodgeic AI';
export const ASSISTANT_OPEN_EVENT = 'lodgeic:assistant:open';

const HIDDEN_ROUTES = [/^\/login/, /^\/auth\//, /\/export$/, /^\/test\//];

const SparkIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
    <path d="M18.5 15.5l.8 1.9 1.9.8-1.9.8-.8 1.9-.8-1.9-1.9-.8 1.9-.8.8-1.9z" />
  </svg>
);

const ExpandIcon = ({ expanded }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {expanded ? <><path d="M9 4v5H4" /><path d="M4 9l6-6" /><path d="M15 20v-5h5" /><path d="M20 15l-6 6" /></>
      : <><path d="M14 4h6v6" /><path d="M20 4l-8 8" /><path d="M10 20H4v-6" /><path d="M4 20l8-8" /></>}
  </svg>
);

const MinimizeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M5 12h14" /></svg>
);

/** Open-state launcher: chevron down, per the design's "Open — collapses". */
const CollapseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
);

function readCorner() {
  try {
    const stored = localStorage.getItem(CORNER_KEY);
    return CORNERS.includes(stored) ? stored : 'bottom-right';
  } catch {
    return 'bottom-right';
  }
}

function readPosition() {
  try {
    const parsed = JSON.parse(localStorage.getItem(POSITION_KEY));
    return Number.isFinite(parsed?.left) && Number.isFinite(parsed?.top) ? parsed : null;
  } catch {
    return null;
  }
}

function sessionTitle(session) {
  return session.title || session.lastMessage || 'Untitled conversation';
}

/**
 * `aiEnabled` gates the ASSISTANT, never the dock. Support lives in this corner
 * now and Intercom's own launcher is hidden, so a flag-off customer must still
 * get a support control — see AssistantDock's support-only mode. Deliberately
 * fail-closed: PostHog returns undefined while flags load, and the caller passes
 * `=== true`, so the assistant never flashes for someone who does not have it.
 */
const AssistantLauncher = ({ aiEnabled = true }) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [corner, setCorner] = useState(readCorner);
  const [panelPosition, setPanelPosition] = useState(readPosition);
  const [dragging, setDragging] = useState(false);
  const [locations, setLocations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [selectedHotelId, setSelectedHotelId] = useState(null);
  const [selectedHotelName, setSelectedHotelName] = useState('');
  const [seedPrompt, setSeedPrompt] = useState(null);
  const [seedContextPrefix, setSeedContextPrefix] = useState(null);
  // A native action to RUN on open (workflow ref or seeded question), as
  // opposed to seedPrompt which only prefills the box for the user to send.
  const [seedAction, setSeedAction] = useState(null);
  const [externalTray, setExternalTray] = useState(null);
  const [newConversationRequest, setNewConversationRequest] = useState(0);
  const [working, setWorking] = useState(false);
  const [readyCount, setReadyCount] = useState(0);
  const [readyKind, setReadyKind] = useState('response');
  const panelRef = useRef(null);
  const openRef = useRef(open);
  const dragRef = useRef(null);

  const location = useLocation();
  const { snapshot, warning, clearSelection } = useAssistantContext();
  const { currentOrganization } = useUserSettingsContext();

  openRef.current = open;

  const contextPropertyIds = snapshot?.envelope?.scope?.propertyIds || [];
  const contextProperty = contextPropertyIds.length === 1 ? contextPropertyIds[0] : null;
  const contextParts = useMemo(() => (
    (snapshot?.label || '').split(' · ').map(s => s.trim()).filter(Boolean)
  ), [snapshot]);

  const loadSessions = useCallback(async () => {
    const orgId = currentOrganization?.id || localStorage.getItem('currentOrgId');
    if (!orgId) return;
    try {
      setSessionsLoading(true);
      const response = await getAIChatSessions(null, orgId);
      if (response.success) setSessions(response.sessions || []);
    } catch (err) {
      console.error('Failed to load AI conversation history:', err);
    } finally {
      setSessionsLoading(false);
    }
  }, [currentOrganization?.id]);

  useEffect(() => {
    if (!aiEnabled) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const response = await getLocations();
        const orgId = String(currentOrganization?.id || localStorage.getItem('currentOrgId') || '');
        const available = (response || []).filter(loc => String(loc.orgId) === orgId);
        if (!cancelled) setLocations(available);
      } catch (err) {
        console.error('Failed to load assistant properties:', err);
      }
    };
    load();
    loadSessions();
    return () => { cancelled = true; };
  }, [currentOrganization?.id, loadSessions, aiEnabled]);

  // Page context follows the user while the mounted conversation survives.
  useEffect(() => {
    if (contextProperty != null) {
      const id = String(contextProperty);
      setSelectedHotelId(id);
      setSelectedHotelName(contextParts[0] || locations.find(l => String(l.id) === id)?.name || '');
    } else if (snapshot && contextPropertyIds.length !== 1) {
      setSelectedHotelId('all');
      setSelectedHotelName('All Locations');
    } else if (!selectedHotelId && locations.length > 0) {
      if (locations.length === 1) {
        setSelectedHotelId(String(locations[0].id));
        setSelectedHotelName(locations[0].name);
      } else {
        setSelectedHotelId('all');
        setSelectedHotelName('All Locations');
      }
    }
  }, [contextProperty, contextPropertyIds.length, contextParts, locations, selectedHotelId, snapshot]);

  useEffect(() => {
    try { localStorage.setItem(CORNER_KEY, corner); } catch { /* private mode */ }
  }, [corner]);

  useEffect(() => {
    if (!panelPosition) return;
    try { localStorage.setItem(POSITION_KEY, JSON.stringify(panelPosition)); } catch { /* private mode */ }
  }, [panelPosition]);

  useEffect(() => {
    if (!dragging) return undefined;
    const move = (event) => {
      if (!dragRef.current || !panelRef.current) return;
      const { offsetX, offsetY } = dragRef.current;
      const width = panelRef.current.offsetWidth;
      const height = panelRef.current.offsetHeight;
      setPanelPosition({
        left: Math.max(8, Math.min(window.innerWidth - width - 8, event.clientX - offsetX)),
        top: Math.max(8, Math.min(window.innerHeight - height - 8, event.clientY - offsetY)),
      });
    };
    const stop = () => {
      dragRef.current = null;
      setDragging(false);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop, { once: true });
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
    };
  }, [dragging]);

  useEffect(() => {
    if (open) setReadyCount(0);
  }, [open]);

  useEffect(() => {
    if (!aiEnabled) return undefined;
    const openAssistant = (event) => {
      const detail = event?.detail || {};
      setOpen(true);
      if (detail.expanded !== undefined) setExpanded(!!detail.expanded);
      if (detail.threadId) setActiveThreadId(detail.threadId);
      if (detail.hotelId) {
        const id = String(detail.hotelId);
        setSelectedHotelId(id);
        setSelectedHotelName(detail.hotelName || locations.find(l => String(l.id) === id)?.name || '');
      }
      if (detail.prompt) setSeedPrompt(detail.prompt);
      if (detail.contextPrefix) setSeedContextPrefix(detail.contextPrefix);
      // detail.action = { prompt, promptKey?, workflowRef?, workflowContext?, label? }
      // Runs immediately instead of waiting for the user to press enter —
      // a toolbar button that opens a prefilled box has not done the thing
      // the button says it does.
      if (detail.action?.prompt) setSeedAction({ ...detail.action, requestedAt: Date.now() });
      window.setTimeout(() => panelRef.current?.querySelector('textarea')?.focus(), 80);
    };
    window.addEventListener(ASSISTANT_OPEN_EVENT, openAssistant);
    return () => window.removeEventListener(ASSISTANT_OPEN_EVENT, openAssistant);
  }, [locations, aiEnabled]);

  useEffect(() => {
    if (!aiEnabled) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setOpen(true);
        window.setTimeout(() => panelRef.current?.querySelector('textarea')?.focus(), 60);
      }
      if (e.key === 'Escape' && open) {
        if (expanded) setExpanded(false);
        else setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, expanded, aiEnabled]);

  // Quiet-on-activity: while the user is scrolling the page the collapsed
  // corner cluster tucks halfway off-edge and fades, so it never sits on the
  // content being read (dense grids like Forecast V3's Net Income band). It
  // comes back after a beat of stillness, or instantly on hover/focus. Never
  // quiets while open, and never while a finished response is waiting — the
  // ready badge is exactly the thing that must stay visible.
  const [quiet, setQuiet] = useState(false);
  const quietTimerRef = useRef(null);
  const quietBlockedRef = useRef(false);
  quietBlockedRef.current = open || readyCount > 0;
  useEffect(() => {
    const onActivity = (event) => {
      if (quietBlockedRef.current) return;
      // Scrolling the assistant's own panel is engagement, not page reading.
      if (panelRef.current && event.target instanceof Node && panelRef.current.contains(event.target)) return;
      setQuiet(true);
      window.clearTimeout(quietTimerRef.current);
      quietTimerRef.current = window.setTimeout(() => setQuiet(false), 1400);
    };
    // Capture catches inner scroll containers (tables, panels), not just the page.
    window.addEventListener('scroll', onActivity, { capture: true, passive: true });
    window.addEventListener('wheel', onActivity, { passive: true });
    window.addEventListener('touchmove', onActivity, { passive: true });
    return () => {
      window.removeEventListener('scroll', onActivity, { capture: true });
      window.removeEventListener('wheel', onActivity);
      window.removeEventListener('touchmove', onActivity);
      window.clearTimeout(quietTimerRef.current);
    };
  }, []);
  useEffect(() => {
    if (open || readyCount > 0) {
      window.clearTimeout(quietTimerRef.current);
      setQuiet(false);
    }
  }, [open, readyCount]);

  const cycleCorner = useCallback(() => {
    setPanelPosition(null);
    try { localStorage.removeItem(POSITION_KEY); } catch { /* private mode */ }
    setCorner(prev => CORNERS[(CORNERS.indexOf(prev) + 1) % CORNERS.length]);
  }, []);

  const startPanelDrag = (event) => {
    if (expanded || event.button !== 0 || event.target.closest('button')) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = { offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top };
    setPanelPosition({ left: rect.left, top: rect.top });
    setDragging(true);
    event.preventDefault();
  };

  const startNewConversation = useCallback(() => {
    setActiveThreadId(null);
    setNewConversationRequest(Date.now());
  }, []);

  const handleHotelChange = useCallback((hotelId) => {
    if (hotelId === 'all') {
      setSelectedHotelId('all');
      setSelectedHotelName('All Locations');
    } else if (hotelId) {
      setSelectedHotelId(String(hotelId));
      setSelectedHotelName(locations.find(l => String(l.id) === String(hotelId))?.name || '');
    } else {
      setSelectedHotelId(null);
      setSelectedHotelName('');
    }
    setActiveThreadId(null);
  }, [locations]);

  const handleScopeRestore = useCallback((hotelId) => {
    if (hotelId === 'all') {
      setSelectedHotelId('all');
      setSelectedHotelName('All Locations');
      return;
    }
    const match = locations.find(l => String(l.id) === String(hotelId));
    if (!match) return;
    setSelectedHotelId(String(hotelId));
    setSelectedHotelName(match.name);
  }, [locations]);

  const deleteSession = async (threadId) => {
    await deleteAIChatSession(threadId);
    setSessions(prev => prev.filter(s => s.threadId !== threadId));
    if (activeThreadId === threadId) startNewConversation();
  };

  const handleResponseReady = useCallback((result) => {
    // The chat allocates its durable thread id after the first response. Keep
    // that id at the launcher level immediately so route changes/remounts do
    // not reopen the transcript as an unbound "new chat" and lose the prior
    // tool-call memory needed by referential follow-ups such as "pause this".
    if (result?.threadId) setActiveThreadId(result.threadId);
    setReadyKind(result?.kind === 'review' ? 'review' : 'response');
    if (!openRef.current) setReadyCount(count => count + 1);
    loadSessions();
  }, [loadSessions]);

  const scopeLine = useMemo(() => {
    const org = currentOrganization?.name || null;
    return [org, contextParts.length > 1 ? contextParts.slice(1).join(' · ') : null]
      .filter(Boolean).join(' · ');
  }, [currentOrganization, contextParts]);

  const hidden = HIDDEN_ROUTES.some(re => re.test(location.pathname));
  if (hidden) return null;

  // Assistant off: the corner still exists, holding support alone. Nothing of
  // the AI renders — no panel, no bubble, no ⌘/ — so the flag genuinely turns
  // the assistant off instead of leaving this one always-on-screen entry point
  // working while every other AI surface is hidden.
  if (!aiEnabled) {
    return (
      <div className={`al-root is-closed ${quiet ? 'is-quiet' : ''}`} data-corner={corner}>
        <AssistantDock corner={corner} />
      </div>
    );
  }

  return (
    <div className={`al-root ${open ? '' : 'is-closed'} ${quiet && !open ? 'is-quiet' : ''}`} data-corner={corner}>
      <div
        className={`al-panel ${open ? 'is-open' : 'is-minimized'} ${expanded ? 'al-panel--expanded' : ''} ${panelPosition && !expanded ? 'is-free' : ''} ${dragging ? 'is-dragging' : ''}`}
        style={panelPosition && !expanded ? { left: panelPosition.left, top: panelPosition.top } : undefined}
        ref={panelRef}
        role="dialog"
        aria-label="Lodgeic AI assistant"
        aria-hidden={!open}
      >
        <aside className="al-workspace-rail">
          <div className="al-rail-brand">
            <span className="al-rail-mark"><SparkIcon size={17} /></span>
            <strong>Lodgeic AI</strong>
          </div>
          <button type="button" className="al-new-chat" onClick={startNewConversation}>
            <Plus size={15} /> New chat
          </button>
          <div className="al-rail-section-label">Recent</div>
          <div className="al-rail-sessions">
            {sessionsLoading && <div className="al-rail-empty"><Loader2 size={14} className="al-spin" /> Loading chats</div>}
            {!sessionsLoading && sessions.slice(0, 12).map(session => (
              <button
                type="button"
                key={session.threadId}
                className={`al-session ${activeThreadId === session.threadId ? 'is-active' : ''}`}
                onClick={() => setActiveThreadId(session.threadId)}
                title={sessionTitle(session)}
              >
                <MessageSquare size={14} />
                <span>{sessionTitle(session)}</span>
              </button>
            ))}
            {!sessionsLoading && sessions.length === 0 && <div className="al-rail-empty">Your conversations will appear here.</div>}
          </div>
          <div className="al-rail-footer">
            <button type="button" onClick={() => setExternalTray('history')}><History size={15} /> All conversations</button>
            <button type="button" onClick={() => setExternalTray('automations')}><CalendarClock size={15} /> Automations</button>
            <button type="button" onClick={() => setExternalTray('settings')}><Settings size={15} /> Settings</button>
          </div>
        </aside>

        <section className="al-workspace">
          <div className="al-head" onPointerDown={startPanelDrag} onDoubleClick={expanded ? undefined : cycleCorner} title={expanded ? undefined : 'Drag to move · double-click to dock to a corner'}>
            <div className="al-head-mark"><SparkIcon size={17} /></div>
            <div className="al-head-text">
              <div className="al-head-title"><strong>Lodgeic AI</strong><span className="al-beta">BETA</span></div>
              {scopeLine && <div className="al-head-scope" title={scopeLine}>{scopeLine}</div>}
            </div>
            <div className="al-head-state">
              {working && <span className="is-working"><Loader2 size={13} className="al-spin" /> Working</span>}
            </div>
            <div className="al-head-actions">
              <button type="button" className="al-icon-btn" onClick={() => setExpanded(v => !v)} aria-label={expanded ? 'Exit full screen' : 'Open full screen'} title={expanded ? 'Exit full screen' : 'Full screen'}>
                <ExpandIcon expanded={expanded} />
              </button>
              <button type="button" className="al-icon-btn" onClick={() => setOpen(false)} aria-label="Minimize the assistant" title="Minimize">
                <MinimizeIcon />
              </button>
            </div>
          </div>

          {(contextParts.length > 0 || warning) && (
            <div className="al-context">
              <span className="al-context-label">ON THIS PAGE</span>
              {contextParts.map((part, i) => <span className="al-context-chip" key={`${part}-${i}`}>{part}</span>)}
              {warning && <button type="button" className="al-context-chip al-context-chip--dropped" title={warning} onClick={clearSelection}>{warning}</button>}
            </div>
          )}

          <div className="al-body">
            <AIChat
              selectedHotelId={selectedHotelId}
              selectedHotelName={selectedHotelName}
              activeThreadId={activeThreadId}
              preserveHistoryOnScopeChange={true}
              onNewConversation={() => setActiveThreadId(null)}
              onSessionUpdated={loadSessions}
              suggestedPrompt={seedPrompt}
              onSuggestedPromptUsed={() => setSeedPrompt(null)}
              contextPrefix={seedContextPrefix}
              onContextPrefixConsumed={() => setSeedContextPrefix(null)}
              pendingAction={seedAction}
              onPendingActionConsumed={() => setSeedAction(null)}
              compact={false}
              suggestedPrompts={null}
              highlightPrompts={HIGHLIGHT_PROMPTS}
              locations={locations}
              onHotelChange={handleHotelChange}
              onScopeRestore={handleScopeRestore}
              chatSessions={sessions}
              isLoadingSessions={sessionsLoading}
              onSelectSession={setActiveThreadId}
              onDeleteSession={deleteSession}
              newConversationRequest={newConversationRequest}
              externalTray={externalTray}
              onExternalTrayConsumed={() => setExternalTray(null)}
              onRunStateChange={setWorking}
              onResponseReady={handleResponseReady}
              organizationId={currentOrganization?.id || null}
            />
          </div>
        </section>
      </div>

      {!open && (
        <>
          {/* Support rides inside the Lodgeic dock rather than beside it —
              Intercom's own launcher is hidden, so this corner is one control
              with the AI as its primary action. */}
          <AssistantDock corner={corner} aiBusy={working}>
            <div className="al-launcher-wrap">
              <button
                type="button"
                className={`al-launcher ${working ? 'al-launcher--working' : ''}`}
                onClick={() => setOpen(true)}
                aria-label={`${readyCount ? `${readyCount} item${readyCount === 1 ? '' : 's'} ready. ` : ''}${LAUNCHER_LABEL} (Command slash)`}
              >
                {working ? <Loader2 className="al-spin" /> : <SparkIcon />}
              </button>
              {readyCount > 0 && <span className="al-badge">{readyCount}</span>}
            </div>
          </AssistantDock>
          <div className={`al-label ${readyCount > 0 ? 'is-ready' : ''}`} aria-hidden="true">
            {readyCount > 0 ? (readyKind === 'review' ? 'Review ready' : 'Response ready') : LAUNCHER_LABEL}<kbd>⌘ /</kbd>
          </div>
        </>
      )}
      {open && <button type="button" className="al-launcher al-launcher--open" onClick={() => setOpen(false)} aria-label="Minimize the assistant"><CollapseIcon /></button>}
    </div>
  );
};

export default AssistantLauncher;
